import React from 'react';
import { twMerge } from 'tailwind-merge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ExternalLink } from 'lucide-react';
import type { BaseCustomProps, CustomComponent } from './types';

/*
[//]: # (Component: GoogleSearch)
[//]: # (Description: Displays Gemini API search results with sources and confidence scores)
[//]: # (Usage: <googlesearch searchEntryPoint="string" sources={[]} segments={[]} queries={[]}>)
[//]: # (Props:)
[//]: # (  - searchEntryPoint?: string - Google search UI HTML)
[//]: # (  - sources?: Array<{uri: string, title: string}> - Reference sources)
[//]: # (  - segments?: Array<{text: string, confidence: number, sources: string[]}> - Text segments)
[//]: # (  - queries?: string[] - Search queries used)
[//]: # (  - error?: string - Optional error message)
[//]: # (Example:)
[//]: # (```markdown)
[//]: # (<googlesearch)
[//]: # (  searchEntryPoint="<div>...</div>")
[//]: # (  sources='[{"uri": "https://example.com", "title": "Example"}]')
[//]: # (  segments='[{"text": "Sample text", "confidence": 0.95, "sources": ["..."]}]')
[//]: # (  queries='["search query"]')
[//]: # (/>)
[//]: # (```)
[//]: # (Notes: All props should be JSON stringified when used in markdown)
*/

// Define interfaces for the data structures
interface Source {
  uri: string;
  title: string;
}

interface Segment {
  text: string;
  confidence: number;
  sources: string[];
}

function parseJsonSafely<T>(data: string | T[] | undefined, fallback: T[] = []): T[] {
  if (typeof data === 'string') {
    try {
      // Remove any leading/trailing whitespace
      const trimmed = data.trim();
      
      // If the string starts with [ and ends with ], try parsing it directly
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed) ? parsed : fallback;
        } catch (e) {
          console.error('Failed first parse attempt:', e);
        }
      }
      
      // If that fails, try parsing it as a JSON string
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch (e) {
      console.error('Failed to parse JSON:', e);
      return fallback;
    }
  }
  return Array.isArray(data) ? data : fallback;
}


// Type the component props properly
interface GoogleSearchProps extends BaseCustomProps {
  searchEntryPoint?: string;
  sources?: string | Source[];
  segments?: string | Segment[];
  queries?: string | string[];
  error?: string;
}

export const GoogleSearch: React.FC<GoogleSearchProps> = ({ 
  searchEntryPoint = '',
  sources = [], 
  segments = [], 
  queries = [],
  className,
  error
}) => {
  // Use the generic parsing function with specific types
  const typedSources = parseJsonSafely<Source>(sources);
  const typedSegments = parseJsonSafely<Segment>(segments);
  const typedQueries = parseJsonSafely<string>(queries);

  if (error && typeof error === 'string') {
    return (
      <>
        <br />
        <Card className={twMerge("w-full bg-red-50", className)}>
          <CardContent className="pt-6">
            <span className="text-red-600">Error parsing Gemini response: {error}</span>
          </CardContent>
        </Card>
        <br />
      </>
    );
  }

  if (!searchEntryPoint) {
    return null;
  }

  return (
    <>
      <br />
      <span className={twMerge("inline-block w-full space-y-4", className)}>
        {/* Sources Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Sources</CardTitle>
          </CardHeader>
          <CardContent>
            <span className="inline-block w-full space-y-2">
              {typedSources.map((source, index) => (
                <a
                  key={index}
                  href={source.uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>{source.title || 'Unnamed Source'}</span>
                </a>
              ))}
            </span>
          </CardContent>
        </Card>

        {/* Text Segments with Confidence */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Supporting Evidence</CardTitle>
          </CardHeader>
          <CardContent>
            <span className="inline-block w-full space-y-4">
              {typedSegments.map((segment, index) => (
                <span key={index} className="inline-block w-full border-l-4 border-blue-500 pl-4">
                  <span className="inline-block mb-2">{segment.text}</span>
                  <span className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="bg-blue-100 dark:bg-blue-900 px-2 py-1 rounded">
                      Confidence: {(segment.confidence * 100).toFixed(1)}%
                    </span>
                    <span>•</span>
                    <span>{segment.sources.length} source{segment.sources.length !== 1 ? 's' : ''}</span>
                  </span>
                </span>
              ))}
            </span>
          </CardContent>
        </Card>

        {/* Search Queries */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Search Queries</CardTitle>
          </CardHeader>
          <CardContent>
            <span className="inline-flex flex-wrap gap-2">
              {typedQueries.map((query, index) => (
                <span
                  key={index}
                  className="bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full text-sm"
                >
                  {query}
                </span>
              ))}
            </span>
          </CardContent>
        </Card>
      </span>
      <br />
    </>
  );
};

export default GoogleSearch;